import React, { useContext } from 'react';
import { NavLink } from 'react-router-dom';
import { AppContext } from '../../context/AppContext';
import { Role } from '../../types';
import { ChartBarIcon, Cog6ToothIcon, UsersIcon, Squares2X2Icon, ClockIcon, MapPinIcon, XMarkIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';

interface SidebarProps {
  isOpen: boolean; // Mobile open state
  onClose: () => void; // Mobile close handler
  isDesktopCollapsed: boolean;
  onDesktopToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, isDesktopCollapsed, onDesktopToggle }) => {
  const { currentUser } = useContext(AppContext);

  const navLinkClasses = ({ isActive }: { isActive: boolean }) =>
    `flex items-center transition-all duration-200 ${isActive
      ? 'bg-[#E2B53F] text-gray-900 font-bold shadow-md'
      : 'text-white/80 hover:bg-white/10 hover:text-white'
    } ${isDesktopCollapsed 
      ? 'justify-center w-12 h-12 rounded-xl mx-auto p-0' 
      : 'px-4 py-3 text-sm font-medium rounded-xl'
    }`;

  const SectionTitle: React.FC<{ title: string }> = ({ title }) => {
    if (isDesktopCollapsed) return <div className="pt-5 pb-2 border-t border-white/10 mt-2"></div>;
    return (
      <div className="pt-5 pb-2">
        <p className="px-4 text-[10px] font-bold text-white/40 uppercase tracking-widest">{title}</p>
      </div>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Sidebar Content */}
      <aside className={`fixed inset-y-0 left-0 z-50 bg-[#27272A] text-white flex-shrink-0 flex flex-col h-full overflow-y-auto transform transition-all duration-300 ease-in-out md:relative md:translate-x-0 ${isOpen ? 'translate-x-0 w-56' : '-translate-x-full w-56'} ${isDesktopCollapsed ? 'md:w-20' : 'md:w-56'}`}>
        <div className={`flex items-center px-4 mb-6 mt-2.5 ${isDesktopCollapsed ? 'justify-center mx-auto' : 'justify-center w-full relative'}`}>
          <div className="flex items-center space-x-3">
            <div className="bg-brand-accent p-2 rounded-xl shadow-lg flex-shrink-0">
              <ChartBarIcon className="h-6 w-6 text-gray-900" />
            </div>
            {!isDesktopCollapsed && (
              <span className="text-xl font-black tracking-tight whitespace-nowrap text-white">
                T-Minus-<span className="text-brand-accent">App</span>
              </span>
            )}
          </div>
          <button onClick={onClose} className="md:hidden absolute right-2 text-gray-400 hover:text-white transition-colors p-1">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <nav className={`flex flex-col space-y-1.5 flex-1 ${isDesktopCollapsed ? 'px-1' : 'px-4'}`}>
          <SectionTitle title="Monitoreo" />
          <NavLink to="/dashboard" onClick={onClose} className={navLinkClasses} title="Dashboard">
            <Squares2X2Icon className={`h-5 w-5 flex-shrink-0 ${isDesktopCollapsed ? '' : 'mr-3'}`} />
            {!isDesktopCollapsed && <span>Dashboard</span>}
          </NavLink>
          <NavLink to="/history" onClick={onClose} className={navLinkClasses} title="Historial & IA">
            <ClockIcon className={`h-5 w-5 flex-shrink-0 ${isDesktopCollapsed ? '' : 'mr-3'}`} />
            {!isDesktopCollapsed && <span>Historial & IA</span>}
          </NavLink>
          <NavLink to="/calendar" onClick={onClose} className={navLinkClasses} title="Planificación y Predicción">
            <CalendarDaysIcon className={`h-5 w-5 flex-shrink-0 ${isDesktopCollapsed ? '' : 'mr-3'}`} />
            {!isDesktopCollapsed && <span>Planificación</span>}
          </NavLink>

          {currentUser?.role === Role.ADMIN && (
            <>
              <SectionTitle title="Infraestructura" />
              <NavLink to="/admin/areas" onClick={onClose} className={navLinkClasses} title="Gestión de Áreas">
                <MapPinIcon className={`h-5 w-5 flex-shrink-0 ${isDesktopCollapsed ? '' : 'mr-3'}`} />
                {!isDesktopCollapsed && <span>Gestión de Áreas</span>}
              </NavLink>
              <NavLink to="/admin/meters" onClick={onClose} className={navLinkClasses} title="Equipos / Medidores">
                <Cog6ToothIcon className={`h-5 w-5 flex-shrink-0 ${isDesktopCollapsed ? '' : 'mr-3'}`} />
                {!isDesktopCollapsed && <span>Equipos / Medidores</span>}
              </NavLink>

              <SectionTitle title="Personal" />
              <NavLink to="/admin/users" onClick={onClose} className={navLinkClasses} title="Usuarios y Roles">
                <UsersIcon className={`h-5 w-5 flex-shrink-0 ${isDesktopCollapsed ? '' : 'mr-3'}`} />
                {!isDesktopCollapsed && <span>Usuarios y Roles</span>}
              </NavLink>
            </>
          )}
        </nav>

        {/* Toggle Button at the bottom for Desktop */}
        <div className="mt-auto p-4 border-t border-white/10 hidden md:block">
          <button
            onClick={onDesktopToggle}
            className="w-full flex items-center justify-center p-2.5 rounded-xl text-white/50 hover:text-white hover:bg-white/10 transition-all duration-200"
            title={isDesktopCollapsed ? "Expandir menú" : "Contraer menú"}
          >
            {isDesktopCollapsed ? (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            ) : (
              <div className="flex items-center justify-between w-full px-2">
                <span className="text-xs font-bold uppercase tracking-wider">Contraer menú</span>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                </svg>
              </div>
            )}
          </button>
        </div>

        <div className={`pt-2 pb-4 px-2 text-center text-[10px] text-white/30 ${isDesktopCollapsed ? 'hidden' : 'block'}`}>
          <p>T-Minus-App v1.2</p>
          <p className="mt-0.5">&copy; {new Date().getFullYear()} Ronald Salazar</p>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
